import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Paper 
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockIcon from '@mui/icons-material/Lock';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';

const UnauthorizedPage = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/login');
  };

  const handleContactSupport = () => {
    window.open('https://airolabs.ai/contact-us/', '_blank');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0D004A 0%, #1a0a6e 100%)',
        p: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper 
          elevation={8}
          sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative background */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(13, 0, 74, 0.1) 0%, rgba(26, 10, 110, 0.05) 100%)',
              zIndex: 0
            }}
          />

          {/* Content */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* Icon */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: '#FFF3E0',
                  mb: 2
                }}
              >
                <LockIcon sx={{ fontSize: 50, color: '#E65100' }} />
              </Box>
            </Box>

            {/* Title */}
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: '#0D004A',
                mb: 2
              }}
            >
              Access Denied
            </Typography>

            {/* Error Icon with Message */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <ErrorOutlineIcon sx={{ color: '#E65100', mr: 1 }} />
              <Typography variant="h6" sx={{ color: '#E65100', fontWeight: 600 }}>
                Unauthorized Email Address
              </Typography>
            </Box>

            {/* Description */}
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                mb: 3,
                lineHeight: 1.7
              }}
            >
              The email address you're trying to use is not registered in our system. 
              This portal is only accessible to authorized Airo Digital Labs employees.
            </Typography>

            {/* Divider */}
            <Box sx={{ 
              borderTop: '2px solid #E0E0E0', 
              my: 3,
              mx: 'auto',
              width: '80%'
            }} />

            {/* Instructions */}
            <Box sx={{ mb: 4, textAlign: 'left', bgcolor: '#F5F5F5', p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#0D004A' }}>
                What to do next:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                • Ensure you're using your official Airo Digital Labs email address
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                • Contact your HR department if you believe this is an error
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                • Reach out to IT support for access-related queries
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                sx={{
                  bgcolor: '#0D004A',
                  color: 'white',
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#1a0a6e'
                  }
                }}
              >
                Back to Login
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleContactSupport}
                sx={{
                  borderColor: '#0D004A',
                  color: '#0D004A',
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#1a0a6e',
                    bgcolor: 'rgba(13, 0, 74, 0.04)'
                  }
                }}
              >
                Contact Support
              </Button>
            </Box>

            {/* Footer Note */}
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                mt: 3,
                color: 'text.disabled'
              }}
            >
              If you need immediate assistance, please contact your system administrator.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default UnauthorizedPage;
